import { EPaginationVariant, Pagination } from '@outdoorsyco/bonfire';
import { useEffect, useRef, useState } from 'react';

import { ILocalityRentalReview } from '@/services/getLocalityRentalReviews';

import { ReviewItem } from './ReviewItem';
import { ReviewsStructuredData } from './ReviewsStructuredData';

type TReviewsProps = {
  url: string;
  urlPath: string;
  reviews: ILocalityRentalReview[];
  localityName: string;
};

export const Reviews = ({ url, urlPath, reviews, localityName }: TReviewsProps) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const CARD_WIDTH = 265;

  const scrollToSlide = (index: number) => {
    if (!scrollContainerRef.current || index < 0 || index >= reviews.length) return;

    scrollContainerRef.current.scrollTo({
      left: CARD_WIDTH * index,
      behavior: 'smooth',
    });
    setActiveSlide(index);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const newIndex = Math.round(scrollLeft / CARD_WIDTH);
      if (newIndex !== activeSlide) setActiveSlide(newIndex);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [activeSlide]);

  return (
    <>
      <ReviewsStructuredData
        url={url}
        urlPath={urlPath}
        reviews={reviews}
        localityName={localityName}
      />

      <div className="flex flex-col items-center w-full h-full px-4">
        <div
          className="relative w-full h-full overflow-x-auto scrollbar-none md:overflow-visible md:flex md:items-start md:justify-between md:gap-6"
          ref={scrollContainerRef}>
          <div className="flex w-full h-full gap-4 md:px-0 md:gap-6">
            {reviews.map(review => (
              <ReviewItem key={review.id} review={review} />
            ))}
          </div>
        </div>

        {/* Mobile pagination dots */}
        <div className="flex mt-4 md:hidden">
          <Pagination
            totalPages={reviews.length}
            pageNumber={activeSlide + 1}
            onChange={(index: number) => scrollToSlide(index - 1)}
            ariaLabel="Reviews pagination"
            variant={EPaginationVariant.Dots}
          />
        </div>
      </div>
    </>
  );
};
